import React from 'react';
import { motion } from 'framer-motion';
import { Calendar, Users } from 'lucide-react';

interface ScheduleResultsCardProps {
  schedule: any;
  onClick: () => void;
}

const ScheduleResultsCard: React.FC<ScheduleResultsCardProps> = ({ schedule, onClick }) => {
  // Extract data with fallbacks
  const {
    session_name = 'Untitled Session',
    start_datetime = '',
    status = 'completed',
    round_type = 'Technical',
  } = schedule;

  const targetCount = Array.isArray(schedule.target_users) && schedule.target_users.length > 0
    ? schedule.target_users.length
    : (typeof schedule.target_users === 'number' && schedule.target_users > 0 ? schedule.target_users : 1);

  const isAttended =
    (Array.isArray(schedule.attended_users) && schedule.attended_users.length > 0) ||
    (typeof schedule.attendance_status === 'string' &&
      schedule.attendance_status.toLowerCase().includes('attend') &&
      !schedule.attendance_status.toLowerCase().includes('not'));

  let attendedCount = 0;
  if (isAttended) {
    if (Array.isArray(schedule.attended_users) && schedule.attended_users.length > 0) {
      attendedCount = Math.min(schedule.attended_users.length, targetCount);
    } else {
      attendedCount = targetCount;
    }
  } else {
    attendedCount = 0;
  }

  const attendancePerc = targetCount > 0 ? Math.round((attendedCount / targetCount) * 100) : 0;

  // SVG Circle calculation
  const radius = 24;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (attendancePerc / 100) * circumference;

  const formatDate = (raw: string) => {
    if (!raw) return 'N/A';
    const iso = raw.replace(/\//g, '-').replace(', ', 'T');
    const d = new Date(iso);
    if (isNaN(d.getTime())) return raw;
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) + ', ' + d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <motion.div
      className="schedule-result-card"
      onClick={onClick}
      whileHover={{ y: -3 }}
      transition={{ duration: 0.2 }}
    >
      <div className={`card-status-indicator status-${status.toLowerCase()}`} />

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
        <div style={{ flex: 1, paddingRight: '16px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
            <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 700, color: '#0f172a', lineHeight: 1.2 }}>{session_name}</h3>
            <span className={`status-badge ${status.toLowerCase()}`} style={{ padding: '2px 8px', fontSize: '12px', lineHeight: 1 }}>{status}</span>
            <span
              style={{
                padding: '2px 8px',
                fontSize: '11px',
                fontWeight: 700,
                borderRadius: '6px',
                backgroundColor: schedule.interview_type === 'EXPERT' ? '#e0e7ff' : '#dcfce7',
                color: schedule.interview_type === 'EXPERT' ? '#3730a3' : '#166534',
                border: `1px solid ${schedule.interview_type === 'EXPERT' ? '#c7d2fe' : '#bbf7d0'}`,
                lineHeight: 1
              }}
            >
              {schedule.interview_type === 'EXPERT' ? 'Expert' : 'AI'}
            </span>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', fontSize: '13px', color: '#64748b', flexWrap: 'wrap' }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <Calendar size={14} strokeWidth={2.5} /> {formatDate(start_datetime)}
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <Users size={14} strokeWidth={2.5} /> {round_type}
            </span>
          </div>

          <div style={{ fontSize: '13px', color: '#475569', fontWeight: 500, marginTop: '2px' }}>
            Attendance: <strong style={{ color: '#0f172a' }}>{attendedCount}</strong> <span style={{ color: '#94a3b8' }}>/ {targetCount}</span>
          </div>
        </div>

        <div style={{ flexShrink: 0 }}>
          <div className="attendance-circle" style={{ width: '50px', height: '50px' }}>
            <svg width="50" height="50" viewBox="0 0 60 60" style={{ transform: 'rotate(-90deg)' }}>
              <circle cx="30" cy="30" r="24" className="bg-circle" />
              <circle
                cx="30"
                cy="30"
                r="24"
                className="progress-circle"
                strokeDasharray={circumference}
                strokeDashoffset={strokeDashoffset}
              />
            </svg>
            <div className="percentage" style={{ fontSize: '13px' }}>{attendancePerc}%</div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default ScheduleResultsCard;
